import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  BrandLookup,
  AuthFieldLookup,
  FleetAuthFieldUpdate,
  FleetConfiguration,
  FleetListResponse,
  FleetLookups,
  FleetSaveResponse,
  MerchantNumberLookup,
  SearchLookupResponse,
  SellerLookup,
  TyreLookup
} from './fleet-maintenance.models';


@Injectable({ providedIn: 'root' })
export class FleetMaintenanceService {
  private readonly apiUrl = environment.ApiUrl() + 'v1/fleet-management/';

  constructor(private http: HttpClient) { }

  getFleets(query = '', includeHidden = true, page = 1, pageSize = 100): Observable<FleetListResponse> {
    const params = new HttpParams()
      .set('query', query)
      .set('includeHidden', includeHidden)
      .set('page', page)
      .set('pageSize', pageSize);
    return this.http.get<FleetListResponse>(this.apiUrl + 'fleets', { params });
  }

  getFleet(fleetId: number): Observable<FleetConfiguration> {
    return this.http.get<FleetConfiguration>(this.apiUrl + `fleets/${fleetId}`).pipe(
      map(configuration => this.normaliseConfigurationNumbers(configuration))
    );
  }

  createFleet(configuration: FleetConfiguration): Observable<FleetSaveResponse> {
    return this.http.post<FleetSaveResponse>(this.apiUrl + 'fleets', configuration);
  }

  updateFleet(configuration: FleetConfiguration): Observable<FleetSaveResponse> {
    return this.http.put<FleetSaveResponse>(
      this.apiUrl + `fleets/${configuration.specialPriceSetId}`,
      configuration
    );
  }

  updateVisibility(fleetId: number, isVisible: boolean, versionHash: string): Observable<FleetSaveResponse> {
    return this.http.patch<FleetSaveResponse>(this.apiUrl + `fleets/${fleetId}/visibility`, {
      isVisible,
      versionHash
    });
  }

  getLookups(): Observable<FleetLookups> {
    return this.http.get<FleetLookups>(this.apiUrl + 'lookups').pipe(
      map(lookups => ({
        ...lookups,
        rimSizes: (lookups.rimSizes || []).map(rim => Number(rim))
      }))
    );
  }

  updateAuthField(fleetAuthFieldId: number, update: FleetAuthFieldUpdate): Observable<AuthFieldLookup> {
    return this.http.put<AuthFieldLookup>(
      this.apiUrl + `lookups/auth-fields/${fleetAuthFieldId}`,
      update
    );
  }

  searchBrands(query: string): Observable<SearchLookupResponse<BrandLookup>> {
    return this.search<BrandLookup>('brands', query);
  }

  searchTyres(query: string): Observable<SearchLookupResponse<TyreLookup>> {
    return this.search<TyreLookup>('tyres', query).pipe(
      map(response => ({
        ...response,
        items: response.items.map(item => ({
          ...item,
          rim: this.nullableNumber(item.rim)
        }))
      }))
    );
  }

  searchSellers(query: string): Observable<SearchLookupResponse<SellerLookup>> {
    return this.search<SellerLookup>('sellers', query, 250);
  }

  searchMerchantNumbers(query: string): Observable<SearchLookupResponse<MerchantNumberLookup>> {
    return this.search<MerchantNumberLookup>('merchant-numbers', query);
  }

  private search<T>(lookup: string, query: string, pageSize = 100): Observable<SearchLookupResponse<T>> {
    const params = new HttpParams()
      .set('query', query)
      .set('page', 1)
      .set('pageSize', pageSize);
    return this.http.get<SearchLookupResponse<T>>(this.apiUrl + `lookups/${lookup}`, { params });
  }

  private normaliseConfigurationNumbers(configuration: FleetConfiguration): FleetConfiguration {
    return {
      ...configuration,
      matrixRules: configuration.matrixRules.map(rule => ({
        ...rule,
        rim: this.nullableNumber(rule.rim),
        value: Number(rule.value)
      })),
      skuExceptions: configuration.skuExceptions.map(item => ({
        ...item,
        price: this.nullableNumber(item.price),
        priceMatrixMethodValue: this.nullableNumber(item.priceMatrixMethodValue)
      }))
    };
  }

  private nullableNumber(value: number | string | null): number | null {
    return value === null ? null : Number(value);
  }
}
